import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Search,
  ExternalLink,
  Pencil,
  Trash2,
  Link2,
  Check,
  ChevronDown,
  ChevronRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Download,
} from "lucide-react";
import * as XLSX from "xlsx";
import { apiService } from "../services/apiService";
import DeleteAllAddressesModal from "../components/modals/DeleteAllAddressesModal";
import DeleteMasterAddressModal from "../components/modals/DeleteMasterAddressModal";
import RemoveDuplicateAddressesModal from "../components/modals/RemoveDuplicateAddressesModal";
import MasterAddressModal from "../components/modals/MasterAddressModal";
import {
  canMutateTenantData,
  INVENTORY_ACCOUNT_SEARCH_KEY,
  MASTER_ADDRESS_SEARCH_KEY,
  OVERVIEW_MASTER_ADDRESS_TAB_KEY,
} from "../config/constants";
import Toast from "../components/ui/Toast";
import { SpinnerCustom } from "../components/ui/Spinner";
import CustomSelect from "../components/ui/CustomSelect";

const display = (v) => {
  const t = v != null ? String(v).trim() : "";
  return t || "—";
};

const SHOW_OPTIONS = [
  { value: "all", label: "All masters" },
  { value: "with_unmapped", label: "With unmapped inputs" },
  { value: "with_3_plus", label: "With 3 or more inputs" },
];

const compareMasterGroups = (a, b, sortBy, sortOrder) => {
  const isDesc = String(sortOrder).toLowerCase() === "desc";
  if (sortBy === "records" || sortBy === "recordCount") {
    const diff = (Number(a.recordCount) || 0) - (Number(b.recordCount) || 0);
    if (diff !== 0) return isDesc ? -diff : diff;
  } else {
    const field =
      sortBy === "std_city" ||
      sortBy === "std_state" ||
      sortBy === "std_zip" ||
      sortBy === "std_street_address" ||
      sortBy === "standard_address"
        ? sortBy === "standard_address"
          ? "std_street_address"
          : sortBy
        : "std_street_address";
    const va = String(a[field] || "").toLowerCase();
    const vb = String(b[field] || "").toLowerCase();
    const cmp = va.localeCompare(vb, undefined, {
      numeric: true,
      sensitivity: "base",
    });
    if (cmp !== 0) return isDesc ? -cmp : cmp;
  }
  return String(a.addressKey || "").localeCompare(String(b.addressKey || ""));
};

const MasterAddressPage = ({
    userRole, 
    tenant, 
    tenantId,
  isEmbedded = false,
  setHeaderOptionsContent,
  onNavigate,
}) => {
  const canEdit = canMutateTenantData(userRole);
  const currentTenantId =
    tenantId || tenant?.id || tenant?.tenant_id || tenant?.tenantId;

  const [activeTab, setActiveTab] = useState(() => {
    try {
      const tab = String(
        sessionStorage.getItem(OVERVIEW_MASTER_ADDRESS_TAB_KEY) || "",
      ).trim();
      if (tab === "needs" || tab === "masters") return tab;
    } catch {
      /* ignore */
    }
    return "masters";
  }); // masters | needs
  const [stats, setStats] = useState({
    master_addresses: 0,
    input_addresses: 0,
    needs_a_master: 0,
  });
  const [masters, setMasters] = useState([]);
  const [needsQueue, setNeedsQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState({ message: null, type: "success" });
  const [exporting, setExporting] = useState(false);

  const [searchQuery, setSearchQuery] = useState(() => {
    try {
      return String(sessionStorage.getItem(MASTER_ADDRESS_SEARCH_KEY) || "").trim();
    } catch {
      return "";
    }
  });
  const [debouncedSearch, setDebouncedSearch] = useState(() => {
    try {
      return String(sessionStorage.getItem(MASTER_ADDRESS_SEARCH_KEY) || "").trim();
    } catch {
      return "";
    }
  });
  const [cityFilter, setCityFilter] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [zipFilter, setZipFilter] = useState("");
  const [debouncedCity, setDebouncedCity] = useState("");
  const [debouncedState, setDebouncedState] = useState("");
  const [debouncedZip, setDebouncedZip] = useState("");
  const [showFilter, setShowFilter] = useState("all");
  const [sortConfig, setSortConfig] = useState({
    sortBy: "std_street_address",
    sortOrder: "asc",
  });
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 50,
    total: 0,
    totalPages: 0,
  });
  const mastersFetchSeq = useRef(0);

  const [expandedKeys, setExpandedKeys] = useState(() => new Set());
  const [selectedNeeds, setSelectedNeeds] = useState(() => new Set());
  const [bulkMasterKey, setBulkMasterKey] = useState("");
  const [masterOptions, setMasterOptions] = useState([]);
  const [importLinkSummary, setImportLinkSummary] = useState(null);

  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [masterPendingDelete, setMasterPendingDelete] = useState(null);
  const [showDedupeModal, setShowDedupeModal] = useState(false);
  const [modal, setModal] = useState({
    open: false,
    mode: "add",
    inputAddress: null,
    currentMaster: null,
    masterToEdit: null,
  });

  // Debounce search + location filters
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 350);
    return () => clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedCity(cityFilter.trim()), 350);
    return () => clearTimeout(t);
  }, [cityFilter]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedState(stateFilter.trim()), 350);
    return () => clearTimeout(t);
  }, [stateFilter]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedZip(zipFilter.trim()), 350);
    return () => clearTimeout(t);
  }, [zipFilter]);

  useEffect(() => {
    try {
      sessionStorage.removeItem(OVERVIEW_MASTER_ADDRESS_TAB_KEY);
      sessionStorage.removeItem(MASTER_ADDRESS_SEARCH_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  // Post-import strip
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("masterAddressLinkSummary");
      if (raw) {
        setImportLinkSummary(JSON.parse(raw));
        sessionStorage.removeItem("masterAddressLinkSummary");
      }
    } catch {
      /* ignore */
    }
  }, []);

  const fetchStats = useCallback(async () => {
    if (!currentTenantId) return;
    try {
      const res = await apiService.getMasterAddressStats(currentTenantId);
      if (res.success && res.data) setStats(res.data);
    } catch (err) {
      console.error(err);
    }
  }, [currentTenantId]);

  const fetchMasters = useCallback(async () => {
    if (!currentTenantId) return;
    const seq = ++mastersFetchSeq.current;
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.getMasterAddresses({
        page: pagination.page,
        pageSize: pagination.pageSize,
        sortBy: sortConfig.sortBy,
        sortOrder: sortConfig.sortOrder,
        search: debouncedSearch,
        city: debouncedCity,
        state: debouncedState,
        zip: debouncedZip,
        show: showFilter,
        tenantId: currentTenantId,
      });
      if (seq !== mastersFetchSeq.current) return;
      if (res.success) {
        const rows = Array.isArray(res.data) ? res.data : [];
        // Keep on-screen order aligned with the active sort (guards stale responses)
        rows.sort((a, b) =>
          compareMasterGroups(a, b, sortConfig.sortBy, sortConfig.sortOrder),
        );
        setMasters(rows);
        setPagination((prev) => ({
          ...prev,
          page: res.pagination?.page ?? prev.page,
          pageSize: res.pagination?.pageSize ?? prev.pageSize,
          total: res.pagination?.total ?? prev.total,
          totalPages: res.pagination?.totalPages ?? prev.totalPages,
        }));
      } else {
        setError(res.message || "Failed to load masters");
      }
    } catch (err) {
      if (seq !== mastersFetchSeq.current) return;
      setError(err.message || "Failed to load masters");
    } finally {
      if (seq === mastersFetchSeq.current) setLoading(false);
    }
  }, [
    currentTenantId,
    pagination.page,
    pagination.pageSize,
    sortConfig.sortBy,
    sortConfig.sortOrder,
    debouncedSearch,
    debouncedCity,
    debouncedState,
    debouncedZip,
    showFilter,
  ]);

  const fetchNeeds = useCallback(async () => {
    if (!currentTenantId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.getNeedsMasterAddresses({
        page: 1,
        pageSize: 100,
        search: debouncedSearch,
        state: debouncedState,
        zip: debouncedZip,
        tenantId: currentTenantId,
      });
      if (res.success) {
        setNeedsQueue(res.data || []);
        setPagination((prev) => ({
          ...prev,
          ...(res.pagination || {}),
            page: 1,
        }));
      } else {
        setError(res.message || "Failed to load queue");
      }
    } catch (err) {
      setError(err.message || "Failed to load queue");
    } finally {
      setLoading(false);
    }
  }, [currentTenantId, debouncedSearch, debouncedState, debouncedZip]);

  const refreshAll = useCallback(async () => {
    await fetchStats();
    if (activeTab === "masters") await fetchMasters();
    else await fetchNeeds();
  }, [fetchStats, fetchMasters, fetchNeeds, activeTab]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    if (activeTab === "masters") fetchMasters();
    else fetchNeeds();
  }, [activeTab, fetchMasters, fetchNeeds]);

  useEffect(() => {
    setPagination((p) => ({ ...p, page: 1 }));
  }, [
    debouncedSearch,
    debouncedCity,
    debouncedState,
    debouncedZip,
    showFilter,
    activeTab,
  ]);

  useEffect(() => {
    if (!canEdit) return;
    apiService
      .getMasterAddressOptions(currentTenantId)
      .then((res) => {
        if (res.success) setMasterOptions(res.data || []);
      })
      .catch(() => {});
  }, [canEdit, currentTenantId, stats.master_addresses]);

  const openInventory = (street) => {
    try {
      sessionStorage.setItem(
        INVENTORY_ACCOUNT_SEARCH_KEY,
        String(street || "").trim()
      );
    } catch {
      /* ignore */
    }
    if (onNavigate) onNavigate("inventory");
  };

  const toggleExpand = (key) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleSort = (field) => {
    setSortConfig((prev) => {
      if (prev.sortBy === field) {
        return {
          sortBy: field,
          sortOrder: prev.sortOrder === "asc" ? "desc" : "asc",
        };
      }
      return { sortBy: field, sortOrder: "asc" };
    });
    // Always return to first page so server pagination matches the new order
    setPagination((prev) => (prev.page === 1 ? prev : { ...prev, page: 1 }));
  };

  const SortIcon = ({ field }) => {
    const active = sortConfig.sortBy === field;
    const iconClass = `w-3.5 h-3.5 ml-1 ${
      active ? "text-brand-red stroke-[2.5]" : "text-gray-500 stroke-[2.25]"
    }`;
    if (active && sortConfig.sortOrder === "asc") {
      return <ArrowUp className={iconClass} aria-hidden />;
    }
    if (active && sortConfig.sortOrder === "desc") {
      return <ArrowDown className={iconClass} aria-hidden />;
    }
    return <ArrowUpDown className={iconClass} aria-hidden />;
  };

  const SortHeader = ({ field, children, className = "" }) => {
    const label = field.replace(/^std_/, "").replace(/_/g, " ");
    const active = sortConfig.sortBy === field;
    return (
      <button
        type="button"
        title={`Sort by ${label}`}
        aria-label={`Sort by ${label}`}
        aria-sort={
          active
            ? sortConfig.sortOrder === "asc"
              ? "ascending"
              : "descending"
            : "none"
        }
        className={`inline-flex items-center hover:text-gray-900 ${className}`}
        onClick={() => handleSort(field)}
      >
        {children}
        <SortIcon field={field} />
      </button>
    );
  };

  const displayedMasters = useMemo(
    () =>
      [...masters].sort((a, b) =>
        compareMasterGroups(a, b, sortConfig.sortBy, sortConfig.sortOrder),
      ),
    [masters, sortConfig.sortBy, sortConfig.sortOrder],
  );

  const confirmSuggestion = async (row) => {
    if (!row?.suggested_master || !row.address_id) return;
    try {
      const res = await apiService.linkAddressesToMaster({
        tenantId: currentTenantId,
        addressIds: [row.address_id],
        master: row.suggested_master,
        howLinked: "confirmed",
      });
      if (!res.success) throw new Error(res.message || "Confirm failed");
      setToast({
        message: "Address linked to master successfully",
        type: "success",
      });
      setSelectedNeeds((prev) => {
        const next = new Set(prev);
        next.delete(row.address_id);
        return next;
      });
      await refreshAll();
    } catch (err) {
      setToast({
        message: err.message || "Failed to confirm the address link",
        type: "error",
      });
    }
  };

  const bulkLink = async () => {
    const ids = Array.from(selectedNeeds);
    const master = masterOptions.find((m) => m.masterKey === bulkMasterKey);
    if (!ids.length || !master) return;
    try {
      const res = await apiService.linkAddressesToMaster({
        tenantId: currentTenantId,
        addressIds: ids,
        master,
        howLinked: "confirmed",
      });
      if (!res.success) throw new Error(res.message || "Link failed");
      setToast({
        message: `Successfully linked ${ids.length} address(es) to the selected master`,
        type: "success",
      });
      setSelectedNeeds(new Set());
      setBulkMasterKey("");
      await refreshAll();
    } catch (err) {
      setToast({
        message: err.message || "Failed to link addresses to master",
        type: "error",
      });
    }
  };

  const resetFilters = () => {
    setSearchQuery("");
    setCityFilter("");
    setStateFilter("");
    setZipFilter("");
    setShowFilter("all");
  };

  const buildMasterListParams = useCallback(
    (page, pageSize) => ({
      page,
      pageSize,
      sortBy: sortConfig.sortBy,
      sortOrder: sortConfig.sortOrder,
      search: debouncedSearch,
      city: debouncedCity,
      state: debouncedState,
      zip: debouncedZip,
      show: showFilter,
      tenantId: currentTenantId,
    }),
    [
      sortConfig,
      debouncedSearch,
      debouncedCity,
      debouncedState,
      debouncedZip,
      showFilter,
      currentTenantId,
    ],
  );

  const handleExport = async () => {
    if (!currentTenantId || exporting || activeTab !== "masters") return;
    setExporting(true);
    try {
      const pageSize = 100;
      let page = 1;
      let totalPages = 1;
      const allRows = [];
      do {
        const res = await apiService.getMasterAddresses(
          buildMasterListParams(page, pageSize),
        );
        if (!res.success) {
          throw new Error(res.message || "Failed to load addresses for export");
        }
        allRows.push(...(res.data || []));
        totalPages = res.pagination?.totalPages || 1;
        page += 1;
      } while (page <= totalPages);

      const exportRows = allRows.map((g) => ({
        "Standardized Address": g.std_street_address || "",
        City: g.std_city || "",
        State: g.std_state || "",
        ZIP: g.std_zip || "",
        Country: g.std_country || "",
        "Site Name": g.site_name || "",
        Records: g.recordCount || 0,
        "Input Addresses": g.totalCount || g.items?.length || 0,
      }));

      const stamp = new Date().toISOString().slice(0, 10);
      const ws = XLSX.utils.json_to_sheet(exportRows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Master Addresses");
      XLSX.writeFile(wb, `master_addresses_${stamp}.xlsx`);

      setToast({
        message: `Exported ${exportRows.length} master address${
          exportRows.length === 1 ? "" : "es"
        }`,
        type: "success",
      });
    } catch (err) {
      setToast({
        message: err.message || "Failed to export address list",
        type: "error",
      });
    } finally {
      setExporting(false);
    }
  };

  // No header options bar — title, stats, and actions live in the page body
  useEffect(() => {
    if (!setHeaderOptionsContent) return undefined;
    setHeaderOptionsContent(null);
    return () => setHeaderOptionsContent(null);
  }, [setHeaderOptionsContent]);

  const pageToast = toast.message ? (
    <div className="fixed top-4 right-4 z-[10000]">
      <Toast
        message={toast.message}
        type={toast.type}
        portal={false}
        className="!relative !top-0 !right-0"
        onClose={() => setToast({ message: null, type: "success" })}
      />
    </div>
  ) : null;

  const allNeedsSelected =
    needsQueue.length > 0 &&
    needsQueue.every((r) => selectedNeeds.has(r.address_id));

  const actionButtons = canEdit ? (
    <div className="flex flex-wrap items-center gap-2 shrink-0">
      <button
        type="button"
        onClick={() => setShowDedupeModal(true)}
        className="px-3.5 py-2 rounded-md text-[13px] font-medium border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 whitespace-nowrap"
      >
        Remove duplicates
      </button>
      <button
        type="button"
        onClick={() => setShowDeleteAllModal(true)}
        className="px-3.5 py-2 rounded-md text-[13px] font-medium border border-brand-red text-brand-red bg-white hover:bg-red-50 whitespace-nowrap"
      >
        Delete
      </button>
      <button
        type="button"
        onClick={() =>
          setModal({
            open: true,
            mode: "add",
            inputAddress: null,
            currentMaster: null,
            masterToEdit: null,
          })
        }
        className="px-3.5 py-2 rounded-md text-[13px] font-medium text-white bg-brand-red hover:bg-red-800 whitespace-nowrap"
      >
        + Add address
      </button>
    </div>
  ) : null;

  if (loading && masters.length === 0 && needsQueue.length === 0) {
    return (
      <>
        {pageToast}
        <div
          className={`min-h-screen flex items-center justify-center ${
            isEmbedded
              ? "bg-transparent"
              : "pt-[80px] bg-[var(--ground,#f5f4f1)]"
          }`}
        >
            <SpinnerCustom />
        </div>
      </>
    );
  }

  return (
    <div
      className={
        isEmbedded
          ? "w-full bg-transparent"
          : "min-h-screen pt-[80px] bg-[var(--ground,#f5f4f1)]"
      }
    >
      <div className="w-full overflow-auto">
        {/* Same content width / side margins as Upload Inventory & Upload Invoice */}
        <div
          className={`page-content has-filter w-full ${
            isEmbedded
              ? "px-0 pt-2 pb-4"
              : "max-w-full lg:max-w-[97%] mx-5 lg:mx-auto mt-2 pb-14"
          }`}
        >
          {/* Page header: title + stats + actions (no navbar options bar) */}
          <div className="mb-5 flex flex-col xl:flex-row xl:items-end xl:justify-between gap-4">
            <div className="min-w-0">
              <p className="text-[12px] font-semibold uppercase tracking-[0.05em] text-brand-red">
                Client Administration
              </p>
              <h1 className="mt-1 text-[26px] font-semibold leading-tight text-gray-900">
                Master Address
              </h1>
            </div>

            <div className="flex flex-wrap items-end gap-3 shrink-0">
              <div className="inline-flex items-stretch overflow-hidden rounded-[10px] border border-gray-200 bg-white shadow-sm divide-x divide-gray-200">
                <div className="px-4 py-2.5 min-w-[120px]">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.05em] text-gray-500">
                    Master addresses
                  </div>
                  <div className="mt-0.5 text-[22px] font-semibold tabular-nums text-gray-900">
                    {stats.master_addresses}
                  </div>
                </div>
                <div className="px-4 py-2.5 min-w-[120px]">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.05em] text-gray-500">
                    Input addresses
                  </div>
                  <div className="mt-0.5 text-[22px] font-semibold tabular-nums text-gray-900">
                    {stats.input_addresses}
                  </div>
                </div>
                <div className="px-4 py-2.5 min-w-[120px]">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.05em] text-gray-500">
                    Needs a master
                  </div>
                  <div className="mt-0.5 text-[22px] font-semibold tabular-nums text-brand-red">
                    {stats.needs_a_master}
                  </div>
                </div>
              </div>
              {actionButtons}
            </div>
          </div>

          {/* Post-import link summary strip */}
          {importLinkSummary && (
            <div className="mb-4 rounded-lg border border-gray-200 bg-white px-4 py-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
              <span className="font-semibold text-gray-800">
                After last import
              </span>
              <span className="text-gray-600">
                Exact match:{" "}
                <strong>{importLinkSummary.exact_match || 0}</strong>
              </span>
              <span className="text-gray-600">
                Previously linked:{" "}
                <strong>{importLinkSummary.previously_linked || 0}</strong>
              </span>
              <span className="text-brand-red">
                Waiting confirmation:{" "}
                <strong>
                  {importLinkSummary.waiting_confirmation || 0}
                </strong>
              </span>
              <span className="text-gray-600">
                No match: <strong>{importLinkSummary.no_match || 0}</strong>
              </span>
              <button
                type="button"
                className="ml-auto text-xs text-gray-500 hover:text-gray-700"
                onClick={() => setImportLinkSummary(null)}
              >
                Dismiss
              </button>
            </div>
          )}

          {error && (
            <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700 flex justify-between">
              <span>{error}</span>
              <button type="button" onClick={() => setError(null)}>
                ×
              </button>
            </div>
          )}

          {/* Tabs with count badges */}
          <div className="flex gap-6 border-b border-gray-200 mb-0">
          <button
              type="button"
              onClick={() => setActiveTab("masters")}
              className={`pb-2.5 text-sm font-semibold border-b-2 -mb-px inline-flex items-center gap-2 ${
                activeTab === "masters"
                  ? "border-brand-red text-brand-red"
                  : "border-transparent text-gray-500"
              }`}
            >
              All masters
              <span
                className={`inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full text-[11px] font-semibold ${
                  activeTab === "masters"
                    ? "bg-red-50 text-brand-red"
                    : "bg-gray-100 text-gray-600"
                }`}
              >
                {stats.master_addresses}
              </span>
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("needs")}
              className={`pb-2.5 text-sm font-semibold border-b-2 -mb-px inline-flex items-center gap-2 ${
                activeTab === "needs"
                  ? "border-brand-red text-brand-red"
                  : "border-transparent text-gray-500"
              }`}
            >
              Needs a master
              <span
                className={`inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full text-[11px] font-semibold ${
                  activeTab === "needs"
                    ? "bg-red-50 text-brand-red"
                    : "bg-gray-100 text-gray-600"
                }`}
              >
                {stats.needs_a_master}
              </span>
            </button>
          </div>

          {/* Filter bar */}
          <div className="mt-0 bg-white border border-t-0 border-gray-200 rounded-b-lg px-4 py-3 flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[200px]">
              <div className="text-[10px] font-semibold text-gray-500 tracking-wide mb-1">
                SEARCH
              </div>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="search"
                  placeholder="Street, city, ZIP or site name"
                  className="w-full border border-gray-300 rounded-md pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-red"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
            <div className="w-[160px]">
              <div className="text-[10px] font-semibold text-gray-500 tracking-wide mb-1">
                CITY
              </div>
              <input
                type="search"
                placeholder="e.g. Sandy Springs"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-red"
                value={cityFilter}
                onChange={(e) => setCityFilter(e.target.value)}
              />
            </div>
            <div className="w-[100px]">
              <div className="text-[10px] font-semibold text-gray-500 tracking-wide mb-1">
                STATE
              </div>
              <input
                type="search"
                placeholder="e.g. GA"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-red"
                value={stateFilter}
                onChange={(e) => setStateFilter(e.target.value)}
              />
            </div>
            <div className="w-[110px]">
              <div className="text-[10px] font-semibold text-gray-500 tracking-wide mb-1">
                ZIP
              </div>
              <input
                type="search"
                placeholder="e.g. 30328"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-red"
                value={zipFilter}
                onChange={(e) => setZipFilter(e.target.value)}
              />
            </div>
            {activeTab === "masters" && (
              <div className="w-[180px]">
                <div className="text-[10px] font-semibold text-gray-500 tracking-wide mb-1">
                  SHOW
                </div>
                <CustomSelect
                  value={showFilter}
                  onChange={(e) => setShowFilter(e.target.value)}
                  options={SHOW_OPTIONS}
                />
              </div>
            )}
            <button
              type="button"
              onClick={resetFilters}
              className="px-3 py-2 text-sm border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50"
            >
              Reset
            </button>
            {activeTab === "masters" && (
              <button
                type="button"
                disabled={exporting}
                onClick={handleExport}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                <Download className="w-4 h-4" />
                {exporting ? "Exporting…" : "Export"}
              </button>
            )}
            <div className="text-sm text-gray-500 ml-auto pb-2">
              {activeTab === "masters"
                ? `Showing ${masters.length} of ${pagination.total || 0} masters`
                : `Showing ${needsQueue.length} of ${pagination.total || 0}`}
            </div>
          </div>

          {activeTab === "needs" && (
            <p className="text-sm text-gray-500 mt-3 mb-2">
              Input addresses that came through an import or invoice and
              don&apos;t belong to a master yet. Suggested matches use the same
              ZIP and street number. Tick several to link them in one go.
            </p>
          )}

          {/* All masters */}
          {activeTab === "masters" && (
            <div className="mt-3 space-y-2">
              <div className="hidden md:grid grid-cols-[28px_minmax(0,2.6fr)_minmax(0,1.2fr)_64px_88px_72px_minmax(72px,0.8fr)_80px] gap-2 px-3 text-[10px] font-semibold tracking-wide text-gray-600 uppercase">
                <span />
                <SortHeader field="std_street_address" className="text-left">
                  Standardized address
                </SortHeader>
                <SortHeader field="std_city" className="text-left">
                  City
                </SortHeader>
                <SortHeader field="std_state" className="text-left">
                  State
                </SortHeader>
                <SortHeader field="std_zip" className="text-left">
                  ZIP
                </SortHeader>
                <span>Country</span>
                <SortHeader field="records" className="justify-end">
                  Records
                </SortHeader>
                <span className="text-right">Site</span>
              </div>

              {displayedMasters.length === 0 ? (
                <div className="bg-white border border-gray-200 rounded-lg p-8 text-center text-gray-500 text-sm">
                  No master addresses yet. Add one, or link inputs from Needs a
                  master.
                </div>
              ) : (
                displayedMasters.map((group) => {
                  const expanded = expandedKeys.has(group.addressKey);
                  const waiting = group.waitingCount || 0;
                  return (
                    <div
                      key={group.addressKey}
                      className={`bg-white rounded-lg border ${
                        waiting > 0
                          ? "border-red-200 shadow-[0_0_0_1px_rgba(165,28,28,0.08)]"
                          : "border-gray-200"
                      }`}
                    >
                      <div
                        className="grid grid-cols-1 md:grid-cols-[28px_minmax(0,2.6fr)_minmax(0,1.2fr)_64px_88px_72px_minmax(72px,0.8fr)_80px] gap-2 items-center px-3 py-3 cursor-pointer"
                        onClick={() => toggleExpand(group.addressKey)}
                      >
                        <span className="text-gray-500">
                          {expanded ? (
                            <ChevronDown className="w-4 h-4" />
                          ) : (
                            <ChevronRight className="w-4 h-4" />
                          )}
                        </span>
                        <div className="min-w-0 flex flex-wrap items-center gap-2">
                          <span className="font-semibold text-gray-900">
                            {display(group.std_street_address)}
                          </span>
                          {group.site_name ? (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                              {group.site_name}
                            </span>
                          ) : null}
                          {waiting > 0 ? (
                            <span className="text-sm font-medium text-brand-red">
                              {waiting} input{waiting === 1 ? "" : "s"} waiting
                            </span>
                          ) : null}
                        </div>
                        <div className="text-sm text-gray-800">
                          {display(group.std_city)}
                        </div>
                        <div className="text-sm text-gray-800">
                          {display(group.std_state)}
                        </div>
                        <div className="text-sm text-gray-800">
                          {display(group.std_zip)}
                        </div>
                        <div className="text-sm text-gray-800">
                          {display(group.std_country)}
                        </div>
                        <div className="text-sm text-gray-800 text-right md:text-center">
                          {(group.recordCount || 0).toLocaleString()}
                        </div>
                        <div
                          className="flex items-center justify-end gap-1"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            type="button"
                            title="Open in Detailed Inventory"
                            className="p-1.5 rounded border border-gray-200 text-gray-500 hover:bg-gray-50"
                            onClick={() =>
                              openInventory(group.std_street_address)
                            }
                          >
                            <ExternalLink className="w-4 h-4" />
                          </button>
                          {canEdit && (
                            <>
                              <button
                                type="button"
                                title="Edit master"
                                className="p-1.5 rounded border border-gray-200 text-gray-500 hover:bg-gray-50"
                                onClick={() =>
                                  setModal({
                                    open: true,
                                    mode: "edit",
                                    inputAddress: null,
                                    currentMaster: null,
                                    masterToEdit: group,
                                  })
                                }
                              >
                                <Pencil className="w-4 h-4" />
                              </button>
                              <button
                                type="button"
                                title="Delete master"
                                className="p-1.5 rounded border border-gray-200 text-brand-red hover:bg-red-50"
                                onClick={() => setMasterPendingDelete(group)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>

                      {expanded && (
                        <div className="border-t border-gray-100 bg-gray-50/80 px-3 py-3">
                          <div className="flex items-center justify-between mb-2">
                            <div className="text-[10px] font-semibold tracking-wide text-gray-500 uppercase">
                              Input addresses linked to this master (
                              {group.totalCount || group.items?.length || 0})
                            </div>
                            {waiting > 0 && (
                              <button
                                type="button"
                                className="text-[11px] font-semibold tracking-wide text-brand-red uppercase hover:underline"
                                onClick={() => {
                                  setCityFilter("");
                                  setStateFilter("");
                                  setSearchQuery("");
                                  // Prefer filtering queue by this master's ZIP
                                  setActiveTab("needs");
                                  if (group.std_zip) {
                                    setZipFilter(String(group.std_zip).slice(0, 5));
                                  } else {
                                    setZipFilter("");
                                  }
                                }}
                              >
                                Review {waiting} waiting
                              </button>
                            )}
                          </div>
                          <div className="overflow-x-auto rounded border border-gray-200 bg-white">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="text-[10px] font-semibold tracking-wide text-gray-400 uppercase border-b border-gray-100">
                                  <th className="text-left px-3 py-2">
                                    Input address
                                  </th>
                                  <th className="text-left px-3 py-2">City</th>
                                  <th className="text-left px-3 py-2">State</th>
                                  <th className="text-left px-3 py-2">ZIP</th>
                                  <th className="text-left px-3 py-2">
                                    Country
                                  </th>
                                  <th className="text-left px-3 py-2">
                                    How linked
                                  </th>
                                  {canEdit && (
                                    <th className="text-left px-3 py-2">
                                      Action
                                    </th>
                                  )}
                                </tr>
                              </thead>
                              <tbody>
                                {(group.items || []).map((item) => (
                                  <tr
                                    key={item.address_id}
                                    className="border-b border-gray-50 last:border-0"
                                  >
                                    <td className="px-3 py-2 align-top">
                                      <div className="font-semibold text-gray-900">
                                        {display(item.street_address)}
                                      </div>
                                      <div className="text-xs text-gray-500">
                                        {item.source_label || "Manual entry"}
                                      </div>
                                    </td>
                                    <td className="px-3 py-2">
                                      {display(item.city)}
                                    </td>
                                    <td className="px-3 py-2">
                                      {display(item.state)}
                                    </td>
                                    <td className="px-3 py-2">
                                      {display(item.zip)}
                                    </td>
                                    <td className="px-3 py-2">
                                      {display(item.country)}
                                    </td>
                                    <td className="px-3 py-2 text-gray-600">
                                      {item.how_linked_label || "—"}
                                    </td>
                                    {canEdit && (
                                      <td className="px-3 py-2">
                                        <button
                                          type="button"
                                          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50"
                                          onClick={() =>
                                            setModal({
                                              open: true,
                                              mode: "change",
                                              inputAddress: item,
                                              currentMaster: group,
                                              masterToEdit: null,
                                            })
                                          }
                                        >
                                          <Link2 className="w-3.5 h-3.5" />
                                          Change master
          </button>
                                      </td>
                                    )}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
        </div>
      )}

          {/* Needs a master — full-width grid: address | equal middle cols | suggested */}
          {activeTab === "needs" && (
            <div className="mt-3 bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div
                className={`hidden md:grid gap-3 px-3 py-2 text-[10px] font-semibold tracking-wide text-gray-400 uppercase border-b border-gray-100 bg-gray-50 items-center ${
                  canEdit
                    ? "grid-cols-[2.25rem_minmax(0,1.6fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.6fr)]"
                    : "grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.6fr)]"
                }`}
              >
                {canEdit && (
                  <div>
                    <input
                      type="checkbox"
                      checked={allNeedsSelected}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedNeeds(
                            new Set(needsQueue.map((r) => r.address_id))
                          );
                        } else {
                          setSelectedNeeds(new Set());
                        }
                      }}
                      className="accent-[#a51c1c]"
                    />
                  </div>
                )}
                <div>Input address</div>
                <div>City</div>
                <div>State</div>
                <div>ZIP</div>
                <div>Country</div>
                <div>Suggested master</div>
              </div>

              {needsQueue.length === 0 ? (
                <div className="px-4 py-10 text-center text-sm text-gray-500">
                  No input addresses waiting for a master.
                </div>
              ) : (
                needsQueue.map((row) => {
                  const hasSuggestion = Boolean(row.suggested_master);
                  return (
                    <div
                      key={row.address_id}
                      className={`grid gap-3 px-3 py-3 text-sm border-b border-gray-50 last:border-0 items-start ${
                        canEdit
                          ? "grid-cols-1 md:grid-cols-[2.25rem_minmax(0,1.6fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.6fr)]"
                          : "grid-cols-1 md:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.6fr)]"
                      }`}
                    >
                      {canEdit && (
                        <div className="pt-0.5">
                          <input
                            type="checkbox"
                            checked={selectedNeeds.has(row.address_id)}
                            onChange={(e) => {
                              setSelectedNeeds((prev) => {
                                const next = new Set(prev);
                                if (e.target.checked) next.add(row.address_id);
                                else next.delete(row.address_id);
                                return next;
                              });
                            }}
                            className="accent-[#a51c1c]"
                          />
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="font-semibold text-gray-900 break-words">
                          {display(row.street_address)}
                        </div>
                        <div className="text-xs text-gray-500 truncate">
                          {row.source_label || "Import"}
                        </div>
                      </div>
                      <div className="min-w-0 break-words text-gray-800">
                        {display(row.city)}
                      </div>
                      <div className="min-w-0 text-gray-800">
                        {display(row.state)}
                      </div>
                      <div className="min-w-0 text-gray-800">
                        {display(row.zip)}
                      </div>
                      <div className="min-w-0 text-gray-800">
                        {display(row.country)}
                      </div>
                      <div className="min-w-0">
                        {hasSuggestion ? (
                          <div className="flex flex-wrap items-center gap-2">
                            <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                            <span className="font-medium text-gray-900 break-words min-w-0">
                              {row.suggested_master.std_street_address}
                            </span>
                            {canEdit && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => confirmSuggestion(row)}
                                  className="px-3 py-1 text-xs font-medium rounded-md text-white bg-brand-red hover:bg-red-800"
                                >
                                  Confirm
                                </button>
                                <button
                                  type="button"
                                  onClick={() =>
                                    setModal({
                                      open: true,
                                      mode: "link",
                                      inputAddress: row,
                                      currentMaster: null,
                                      masterToEdit: null,
                                    })
                                  }
                                  className="px-3 py-1 text-xs font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                                >
                                  Other…
                                </button>
                              </>
                            )}
                          </div>
                        ) : (
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-gray-400">No match</span>
                            {canEdit && (
                              <button
                                type="button"
                                onClick={() =>
                                  setModal({
                                    open: true,
                                    mode: "link",
                                    inputAddress: row,
                                    currentMaster: null,
                                    masterToEdit: null,
                                  })
                                }
                                className="px-3 py-1 text-xs font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                              >
                                Link…
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>

      {/* Bulk action bar */}
      {canEdit && activeTab === "needs" && selectedNeeds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] bg-gray-900 text-white rounded-lg shadow-xl px-4 py-3 flex flex-wrap items-center gap-3 min-w-[320px] max-w-[90vw]">
          <span className="text-sm font-medium">
            {selectedNeeds.size} selected
          </span>
          <div className="min-w-[240px] max-w-[360px] text-gray-900">
            <CustomSelect
              value={bulkMasterKey}
              onChange={(e) => setBulkMasterKey(e.target.value)}
              placeholder="Pick a master…"
              aria-label="Pick a master"
              options={[
                { value: "", label: "Pick a master…" },
                ...masterOptions.map((m) => ({
                  value: m.masterKey,
                  label: `${m.std_street_address}${
                    m.site_name ? ` (${m.site_name})` : ""
                  } — ${m.std_city}, ${m.std_state}`,
                })),
              ]}
            />
          </div>
          <button
            type="button"
            disabled={!bulkMasterKey}
            onClick={bulkLink}
            className="px-3 py-1.5 text-sm font-medium rounded-md bg-brand-red hover:bg-red-700 disabled:opacity-40"
          >
            Link to master
          </button>
          <button
            type="button"
            className="text-sm text-gray-300 hover:text-white ml-auto"
            onClick={() => setSelectedNeeds(new Set())}
          >
            Clear
          </button>
        </div>
      )}

      <MasterAddressModal
        open={modal.open}
        mode={modal.mode}
        onClose={() => setModal((m) => ({ ...m, open: false }))}
        onSuccess={async () => {
          const successMessages = {
            add: "Master address added successfully",
            edit: "Master address updated successfully",
            link: "Address linked to master successfully",
            change: "Master address reassigned successfully",
          };
          setToast({
            message:
              successMessages[modal.mode] ||
              "Master address saved successfully",
            type: "success",
          });
          await refreshAll();
        }}
        tenantId={currentTenantId}
          tenant={tenant}
        inputAddress={modal.inputAddress}
        currentMaster={modal.currentMaster}
        masterToEdit={modal.masterToEdit}
        />

      {showDeleteAllModal && (
        <DeleteAllAddressesModal
          onClose={() => setShowDeleteAllModal(false)}
          onSuccess={async () => {
            setShowDeleteAllModal(false);
            setToast({
              message: "All addresses deleted successfully",
              type: "success",
            });
            await refreshAll();
          }}
          tenant={tenant}
          tenantId={tenantId}
        />
      )}

      <DeleteMasterAddressModal
        open={Boolean(masterPendingDelete)}
        master={masterPendingDelete}
        tenantId={currentTenantId}
        onClose={() => setMasterPendingDelete(null)}
        onSuccess={async () => {
          const label =
            masterPendingDelete?.std_street_address || "Master address";
          setMasterPendingDelete(null);
          setToast({
            message: `Deleted “${label}”`,
            type: "success",
          });
          await refreshAll();
        }}
      />

      {showDedupeModal && (
        <RemoveDuplicateAddressesModal
          onClose={() => setShowDedupeModal(false)}
          onSuccess={async () => {
            setShowDedupeModal(false);
            await refreshAll();
          }}
          tenant={tenant}
          tenantId={tenantId}
        />
      )}

      {pageToast}
    </div>
  );
};

export default MasterAddressPage;
